import {
  ChargeCardPaymentSource,
  InternalChargeCardAccount,
  InternalChargeCardActionType,
  Query,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useMakePayment } from 'features/believe/makePayment/hooks/useMakePayment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import queryChargeCardPaymentOptions from 'services/graphql/queries/Believe/ChargeCardPaymentOptions';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import {
  ErrorScreenProps,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { Maybe } from 'shared/utils/types';
import { CombinedError, useQuery } from 'urql';

interface ChargeCardPaymentResult {
  result: ChargeCardPaymentSource[];
  error?: CombinedError;
  errorModel?: Maybe<ErrorScreenProps>;
  loading: boolean;
  refetch: () => void;
}

const useChargeCardPaymentOptions = (): ChargeCardPaymentResult => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'makePayment',
  });
  const [response, refetch] = useQuery<Query>({
    query: queryChargeCardPaymentOptions,
    variables: { id: id },
    requestPolicy: 'cache-and-network',
  });

  const { data, fetching, error } = response;

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const makePaymentContext = useMakePayment();

  const cancel = () => {
    router.back();
  };

  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = error?.message
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: error.message ?? defaultMessage,
        ...buttons,
      }
    : undefined;
  const paymentOptions = data?.chargeCardPaymentOptions ?? null;
  const chargeCardPaymentOptions: ChargeCardPaymentSource = {
    id: 'paybycheck',
    title: translatedText('payByCheck.title'),
    description: '',
    options: [],
  };

  const allSources = [
    ...(paymentOptions?.sources ?? []),
    chargeCardPaymentOptions,
  ] as ChargeCardPaymentSource[];

  useEffect(() => {
    const fullName = data?.user?.customerInfo?.name?.full;
    const accountInfo = data?.accountR2 as InternalChargeCardAccount;
    const pastDue =
      accountInfo?.action?.type === InternalChargeCardActionType.PastDue;
    const paymentDue =
      accountInfo?.action?.type === InternalChargeCardActionType.PaymentDue;
    makePaymentContext?.setFullName(fullName ?? '');
    makePaymentContext?.setPaymentDue(pastDue || paymentDue);
  }, [
    data?.accountR2,
    data?.user?.customerInfo?.name?.full,
    makePaymentContext,
  ]);
  return {
    result: allSources,
    loading: fetching,
    error,
    errorModel,
    refetch,
  };
};

export default useChargeCardPaymentOptions;
